import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getStartup, submitReport } from "../lib/api.js";
import VoteWidget from "../components/VoteWidget.js";
import VibeBar from "../components/VibeBar.js";
import BackedBySection from "../components/BackedBySection.js";
import { HORIZON_LABELS, REASON_TAGS, getCategoryColor } from "../lib/constants.js";
import TurnstileWidget from "../components/TurnstileWidget.js";

function vibeScoreDisplay(score: number): number {
  return Math.round(((score + 2) / 4) * 100);
}

function vibeGradient(score: number): string {
  if (score >= 1) return "from-emerald-500/15 to-green-500/10 border-emerald-500/20";
  if (score >= 0.3) return "from-lime-500/15 to-emerald-500/10 border-lime-500/20";
  if (score >= -0.3) return "from-yellow-500/15 to-amber-500/10 border-yellow-500/20";
  if (score >= -1) return "from-orange-500/15 to-red-500/10 border-orange-500/20";
  return "from-red-500/15 to-rose-500/10 border-red-500/20";
}

function vibeTextColor(score: number): string {
  if (score >= 1) return "text-emerald-400";
  if (score >= 0.3) return "text-lime-400";
  if (score >= -0.3) return "text-yellow-400";
  if (score >= -1) return "text-orange-400";
  return "text-red-400";
}

function fragilityLabel(score: number): string {
  if (score <= 1.5) return "Very Resilient";
  if (score <= 2.5) return "Resilient";
  if (score <= 3.5) return "Mixed";
  if (score <= 4.5) return "Fragile";
  return "Very Fragile";
}

function fragilityGradient(score: number): string {
  if (score <= 2) return "from-emerald-500/15 to-green-500/10 border-emerald-500/20";
  if (score <= 3.5) return "from-yellow-500/15 to-amber-500/10 border-yellow-500/20";
  return "from-red-500/15 to-orange-500/10 border-red-500/20";
}

function confidenceLabel(score: number): string {
  if (score >= 60) return "High";
  if (score >= 30) return "Medium";
  return "Low";
}

function categoryLabel(cat: string): string {
  return cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function StartupDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [startup, setStartup] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getStartup(slug)
      .then(setStartup)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleShareX() {
    const text = `Check out ${startup.name} on StartupVibes \u2014 vibe score: ${vibeScoreDisplay(startup.vibe_score)}/100`;
    const url = window.location.href;
    window.open(
      `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      "_blank"
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto animate-pulse space-y-6">
        <div className="h-4 skeleton rounded w-24" />
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 skeleton rounded-2xl" />
          <div className="flex-1 space-y-3">
            <div className="h-8 skeleton rounded w-1/3" />
            <div className="h-4 skeleton rounded w-2/3" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 skeleton rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !startup) {
    return (
      <div className="text-center py-20 animate-fade-in">
        <div className="text-5xl mb-4">{"🔍"}</div>
        <h1 className="text-2xl font-bold text-slate-300 mb-2">
          Startup not found
        </h1>
        <p className="text-slate-500 mb-6">
          This startup might not exist or was removed.
        </p>
        <Link to="/" className="btn-secondary">
          {"\u2190"} Back to feed
        </Link>
      </div>
    );
  }

  const vibeDisplay = vibeScoreDisplay(startup.vibe_score);
  const color = vibeTextColor(startup.vibe_score);
  const isTrending = startup.trend_score > 0;

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* Back nav */}
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-300 transition-colors mb-6 group"
      >
        <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to feed
      </Link>

      {/* Header */}
      <div className="flex items-start gap-5 mb-8">
        <div className="w-20 h-20 rounded-2xl bg-slate-800/80 flex items-center justify-center overflow-hidden shrink-0 border border-slate-700/30">
          {startup.logo_url ? (
            <img
              src={startup.logo_url}
              alt={startup.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-3xl font-bold text-slate-500">
              {startup.name.charAt(0)}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-extrabold text-white">
              {startup.name}
            </h1>
            {isTrending && (
              <span className="badge-trending">{"🔥"} Trending</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <a
              href={startup.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-400 hover:text-indigo-300 text-sm transition-colors"
            >
              {startup.domain} {"\u2197"}
            </a>
            <span
              className={`badge border text-xs ${getCategoryColor(startup.category)}`}
            >
              {categoryLabel(startup.category)}
            </span>
            {startup.city && (
              <span className="text-sm text-slate-500">
                {"📍"} {startup.city}
                {startup.country ? `, ${startup.country}` : ""}
              </span>
            )}
          </div>
          {startup.description && (
            <p className="text-slate-400 mt-3 leading-relaxed">
              {startup.description}
            </p>
          )}

          {/* Share buttons */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleCopyLink}
              className="btn-ghost text-xs flex items-center gap-1.5 !px-3 !py-1.5 border border-slate-700/30"
            >
              {copied ? (
                <>
                  <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copy link
                </>
              )}
            </button>
            <button
              onClick={handleShareX}
              className="btn-ghost text-xs flex items-center gap-1.5 !px-3 !py-1.5 border border-slate-700/30"
            >
              Share on X
            </button>
          </div>
        </div>
      </div>

      {/* Backers */}
      {startup.backers && startup.backers.length > 0 && (
        <BackedBySection backers={startup.backers} />
      )}

      {/* Score cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div
          className={`bg-gradient-to-br ${vibeGradient(startup.vibe_score)} border rounded-2xl p-5 text-center`}
        >
          <div className="text-sm text-slate-400 mb-2 font-medium">
            Vibe Score
          </div>
          <div className={`text-4xl font-extrabold ${color}`}>
            {vibeDisplay}
          </div>
          <div className="text-xs text-slate-500 mt-1">/ 100</div>
        </div>
        <div
          className={`bg-gradient-to-br ${fragilityGradient(startup.fragility_score)} border rounded-2xl p-5 text-center`}
        >
          <div className="text-sm text-slate-400 mb-2 font-medium">
            Outlook
          </div>
          <div className="text-2xl font-bold text-white">
            {fragilityLabel(startup.fragility_score)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-2xl p-5 text-center">
          <div className="text-sm text-slate-400 mb-2 font-medium">
            Confidence
          </div>
          <div className="text-2xl font-bold text-white">
            {confidenceLabel(startup.confidence_score)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {startup.confidence_score}/100
          </div>
        </div>
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border border-slate-700/30 rounded-2xl p-5 text-center">
          <div className="text-sm text-slate-400 mb-2 font-medium">Votes</div>
          <div className="text-2xl font-bold text-white">
            {startup.trusted_vote_count}
          </div>
          <div className="text-xs text-slate-500 mt-1">trusted</div>
        </div>
      </div>

      {/* Distributions */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Vibe distribution */}
        <div className="card">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <span>{"📊"}</span> Vibe Distribution
          </h3>
          <VibeBar distribution={startup.vibe_distribution} size="lg" />
          <div className="grid grid-cols-5 gap-1 mt-3">
            {[-2, -1, 0, 1, 2].map((v) => (
              <div key={v} className="text-center">
                <div className="text-lg font-bold text-slate-300">
                  {startup.vibe_distribution[v] || 0}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Horizon distribution */}
        <div className="card">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <span>{"⏳"}</span> Survival Horizon
          </h3>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((h) => {
              const count = startup.horizon_distribution?.[h] || 0;
              const total = Object.values(
                startup.horizon_distribution || {}
              ).reduce((sum: number, n: any) => sum + n, 0) as number;
              const pct = total > 0 ? (count / total) * 100 : 0;

              return (
                <div key={h} className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 w-24 shrink-0 font-medium">
                    {HORIZON_LABELS[h]}
                  </span>
                  <div className="flex-1 h-2.5 bg-slate-800/60 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 w-8 text-right font-medium">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tags */}
      {startup.tag_distribution &&
        Object.keys(startup.tag_distribution).length > 0 && (
          <div className="card mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <span>{"🏷️"}</span> Top Tags
            </h3>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(startup.tag_distribution)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .map(([tag, count]) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full bg-slate-800/60 text-sm text-slate-300 border border-slate-700/30 font-medium"
                  >
                    {REASON_TAGS.find((r) => r.value === tag)?.label || tag}{" "}
                    <span className="text-slate-500 ml-1">
                      {count as number}
                    </span>
                  </span>
                ))}
            </div>
          </div>
        )}

      {/* Vote widget */}
      <VoteWidget
        startupId={startup.id}
        startupName={startup.name}
        onVoted={() => {
          if (slug) getStartup(slug).then(setStartup);
        }}
      />

      {/* Report */}
      <div className="mt-8 text-center">
        <button
          onClick={() => setShowReport(true)}
          className="text-sm text-slate-600 hover:text-slate-400 transition-colors"
        >
          {"🚩"} Report this startup
        </button>
      </div>

      {/* Report Modal */}
      {showReport && (
        <ReportModal
          startupId={startup.id}
          startupName={startup.name}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}

function ReportModal({
  startupId,
  startupName,
  onClose,
}: {
  startupId: string;
  startupName: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");

  const reasons = [
    { value: "spam", label: "Spam or fake" },
    { value: "duplicate", label: "Duplicate entry" },
    { value: "offensive", label: "Offensive content" },
    { value: "wrong_info", label: "Wrong information" },
    { value: "other", label: "Other" },
  ];

  async function handleReport() {
    if (!reason || !turnstileToken) return;
    setLoading(true);
    setError(null);
    try {
      await submitReport({
        startup_id: startupId,
        reason_code: reason,
        details: details || undefined,
        turnstile_token: turnstileToken,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative glass p-6 w-full max-w-md space-y-4 animate-scale-in">
        {submitted ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-2">{"\u2705"}</div>
            <h3 className="font-bold text-lg text-white mb-1">
              Report submitted
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Thanks for helping keep StartupVibes clean.
            </p>
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
          </div>
        ) : (
          <>
            <h3 className="font-bold text-lg text-white">
              Report {startupName}
            </h3>
            <div className="space-y-2">
              {reasons.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setReason(r.value)}
                  className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    reason === r.value
                      ? "bg-red-500/15 text-red-300 border border-red-500/30"
                      : "bg-slate-800/60 text-slate-300 border border-slate-700/30 hover:bg-slate-700/60"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Optional details..."
              className="input text-sm h-20 resize-none"
            />
            <TurnstileWidget onToken={setTurnstileToken} />
            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}
            <div className="flex gap-3">
              <button onClick={onClose} className="btn-ghost flex-1">
                Cancel
              </button>
              <button
                onClick={handleReport}
                disabled={!reason || !turnstileToken || loading}
                className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 font-medium py-2.5 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-red-500/20"
              >
                {loading ? "Submitting..." : "Submit Report"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
